// ============================================================================
// useIsAdminUser - single source of truth for client-side admin detection
// ============================================================================
// Matches the server's verifyAdminInline algorithm: users/{uid}.isAdmin ===
// true OR users/{uid}.role === 'admin'. Also honors the legacy
// REACT_APP_ADMIN_UID env var as a fast-path so a freshly-deployed
// environment without the Firestore field still works for the env-set UID.
//
// Use this anywhere the UI needs to gate admin chrome - AdminRoute, ProfilePage
// admin menu entry, GamePage AdminTableControls, TournamentsPage force-start
// button. Single hook = consistent behavior, single place to change.
// ============================================================================
import { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';

const ADMIN_UID = process.env.REACT_APP_ADMIN_UID;

export function useIsAdminUser(uidOverride?: string | null): boolean {
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const uid = uidOverride ?? auth.currentUser?.uid;
    if (!uid) {
      setIsAdmin(false);
      return;
    }
    // Fast path - legacy env-var match.
    if (ADMIN_UID && uid === ADMIN_UID) {
      setIsAdmin(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'users', uid));
        if (cancelled) return;
        if (snap.exists()) {
          const d = snap.data();
          setIsAdmin(d.isAdmin === true || d.role === 'admin');
        } else {
          setIsAdmin(false);
        }
      } catch {
        if (!cancelled) setIsAdmin(false);
      }
    })();
    return () => { cancelled = true; };
  }, [uidOverride]);

  return isAdmin;
}
